import type { BookmarkSectionBatchTarget, ViewMode } from '../types/api';

export interface BuildBookmarkSectionParamsInput {
  viewMode: ViewMode;
  sort: 'newest' | 'oldest';
  folderId?: string;
  /** Same string passed to `client.bookmarks` for `tag` (route slug). */
  tagApiParam?: string;
}

/**
 * Builds the `section` object for POST /data/bookmarks/actions `section_action`,
 * matching `client.bookmarks` / GET /data/bookmarks semantics.
 * Returns null for list modes that do not support section batch (e.g. highlights).
 */
export function buildBookmarkSectionBatchTarget(
  input: BuildBookmarkSectionParamsInput,
): BookmarkSectionBatchTarget | null {
  const { viewMode, sort, folderId, tagApiParam } = input;

  if (viewMode === 'highlights') {
    return null;
  }

  if (viewMode === 'home' || viewMode === 'unread') {
    return { section_type: 'home', sort };
  }

  if (viewMode === 'liked') {
    return { section_type: 'liked', sort };
  }

  if (viewMode === 'archive') {
    return { section_type: 'archive', sort };
  }

  if (viewMode === 'videos') {
    return { section_type: 'videos', sort };
  }

  if (viewMode === 'folder') {
    if (!folderId) return null;
    return { section_type: 'folder', folder_id: folderId, sort };
  }

  if (viewMode === 'tags') {
    if (!tagApiParam) return null;
    return { section_type: 'tag', tag: tagApiParam, sort };
  }

  return null;
}

/** Deep equality for section batch API objects (folder/tag ids coerced to string). */
export function bookmarkSectionBatchTargetsEqual(
  a: BookmarkSectionBatchTarget,
  b: BookmarkSectionBatchTarget,
): boolean {
  if (a.section_type !== b.section_type || a.sort !== b.sort) {
    return false;
  }
  if (a.section_type === 'folder') {
    return String(a.folder_id) === String(b.folder_id);
  }
  if (a.section_type === 'tag') {
    return String(a.tag) === String(b.tag);
  }
  return true;
}

/** Show “whole section selected” summary when batch targets the current list section. */
export function shouldShowBatchSummary(input: {
  isBatchMode: boolean;
  sectionBatchTarget: BookmarkSectionBatchTarget | null;
  sectionDescriptor: BookmarkSectionBatchTarget | null;
}): boolean {
  const { isBatchMode, sectionBatchTarget, sectionDescriptor } = input;
  if (!isBatchMode || !sectionBatchTarget || !sectionDescriptor) {
    return false;
  }
  return bookmarkSectionBatchTargetsEqual(sectionBatchTarget, sectionDescriptor);
}

/** Visibility rules for the batch “select whole section” CTA (list page + batch bar). */
export function shouldShowBatchSelectSectionCta(input: {
  isBatchMode: boolean;
  sectionBatchTarget: BookmarkSectionBatchTarget | null;
  allBookmarkIds: string[];
  selectedBookmarkIds: Set<string>;
  hasMore: boolean;
  pageInfoCount: number | null | undefined;
  sectionDescriptor: BookmarkSectionBatchTarget | null;
}): boolean {
  const {
    isBatchMode,
    sectionBatchTarget,
    allBookmarkIds,
    selectedBookmarkIds,
    hasMore,
    pageInfoCount,
    sectionDescriptor,
  } = input;

  if (!isBatchMode || sectionBatchTarget || !sectionDescriptor) {
    return false;
  }

  const allSelected =
    allBookmarkIds.length > 0 &&
    allBookmarkIds.every((id) => selectedBookmarkIds.has(String(id)));

  if (!allSelected) {
    return false;
  }

  const sectionLargerThanPage =
    hasMore ||
    (typeof pageInfoCount === 'number' && pageInfoCount > allBookmarkIds.length);

  return sectionLargerThanPage;
}
